import React from 'react';
import { Ghost } from 'lucide-react';
import { KakoCoin } from '../oil/OilIcons';
import LAYOUT from './podiumLayout.json';

/* Pódio do Top 3 sobre a moldura gerada: avatares atrás dos aros, nome/valor sobre as placas.
   As posições (pixels medidos na arte) vêm de podiumLayout.json. 1º no centro, 2º à esquerda, 3º à direita. */
const pct = (L, b) => ({ left: `${(b.x / L.W) * 100}%`, top: `${(b.y / L.H) * 100}%`, width: `${(b.w / L.W) * 100}%`, height: `${(b.h / L.H) * 100}%` });
const ORDER = [1, 0, 2];   // rows[0] → círculo do meio, rows[1] → esquerda, rows[2] → direita

const AV = ({ row, className = '' }) => (
  row?.avatar
    ? <img src={row.avatar} alt="" className={`h-full w-full rounded-full object-cover ${className}`} loading="lazy" />
    : <div className={`flex h-full w-full items-center justify-center rounded-full bg-gradient-to-b from-purple-900 to-black text-purple-300 ${className}`}><Ghost className="h-1/2 w-1/2" /></div>
);

/* Etiqueta pendurada na placa: quanto aquela posição levaria se o evento fechasse agora. */
const PrizeTag = ({ text, big, testid }) => (
  <span data-testid={testid} className={`wb-prize-tag ${big ? 'big' : ''}`}>
    <KakoCoin className={big ? 'h-[1.5em]' : 'h-[1.4em]'} /> {text}
  </span>
);

export const WbPodium = ({ kind, rows, label, value, prize, testid }) => {
  const L = LAYOUT[`podium_${kind}`];
  const top = rows.slice(0, 3);
  const temPremio = prize && top.some((_, i) => prize(i + 1));
  return (
    <div className="relative mx-auto w-full max-w-[430px]" data-testid={testid}>
      <div className="relative" style={{ aspectRatio: `${L.W}/${L.H}` }}>
        {top.map((r, i) => {
          const c = L.circles[ORDER[i]];
          return (
            <div key={`av-${i}`} className={`absolute z-0 overflow-hidden rounded-full ${r.me ? 'wb-me-glow' : ''}`} style={{ ...pct(L, c), transform: 'scale(1.04)' }}
              data-testid={`${testid}-avatar-${i + 1}`} data-me={r.me ? '1' : undefined}>
              <AV row={r} />
            </div>
          );
        })}
        <img src={`/wb/podium_${kind}.webp`} alt="" className="pointer-events-none absolute inset-0 z-10 h-full w-full object-contain drop-shadow-2xl" />
        {[0, 1, 2].map((i) => {
          const r = top[i];
          const p = L.plates[ORDER[i]];
          if (!p) return null;
          const big = i === 0;
          const prem = r && prize ? prize(i + 1) : null;
          return (
            <React.Fragment key={`pl-${i}`}>
              <div className="absolute z-20 flex flex-col items-center justify-center overflow-hidden px-[2%] leading-none" style={pct(L, p)} data-testid={`${testid}-${i + 1}`}>
                {r ? (
                  <>
                    <p className={`w-full truncate text-center font-black ${r.me ? 'text-lime-200' : 'text-amber-100'}`} style={{ fontSize: big ? 'clamp(9px,2.9vw,13px)' : 'clamp(7px,2.2vw,10px)' }}>
                      {r.me && <span className="mr-1 rounded-sm bg-lime-400 px-1 text-[0.7em] text-black">VOCÊ</span>}{label(r)}
                    </p>
                    <p className="mt-[0.35em] w-full truncate text-center font-black text-lime-300" style={{ fontSize: big ? 'clamp(8px,2.4vw,11px)' : 'clamp(6px,1.9vw,9px)' }}>{value(r)}</p>
                  </>
                ) : (
                  <p className="text-center font-black uppercase tracking-[.25em] text-purple-300/50" style={{ fontSize: 'clamp(7px,2vw,9px)' }} data-testid={`${testid}-${i + 1}-vago`}>vago</p>
                )}
              </div>
              {prem && (
                <div className="absolute z-30 flex justify-center" style={{ left: `${((p.x + p.w / 2) / L.W) * 100}%`, top: `${((p.y + p.h) / L.H) * 100}%`, transform: 'translate(-50%, 6%)' }}>
                  <PrizeTag text={prem} big={big} testid={`${testid}-prize-${i + 1}`} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
      {temPremio && (
        <p className="mt-4 pb-3 text-center text-[9px] font-bold uppercase tracking-[.2em] text-amber-500/80" data-testid={`${testid}-legend`}>
          prêmios fixos · liberados no encerramento
        </p>
      )}
    </div>
  );
};

/* Lista do 4º em diante dentro da moldura de galhos (border-image). */
export const WbRankList = ({ rows, startAt = 4, label, value, prize, testid, empty }) => (
  <div className="relative mx-auto -mt-2 w-full max-w-[430px]" data-testid={`${testid}-frame`}
    style={{ borderStyle: 'solid', borderWidth: '43px 42px 40px 41px', borderImage: "url('/wb/frame_list.webp') 15.4% 9.8% 14.5% 9.7% round" }}>
    <div className="max-h-[300px] overflow-y-auto bg-[#120a1f] px-1 py-1" data-testid={testid}>
      {rows.length === 0 ? (
        <p className="py-5 text-center text-[11px] font-bold text-purple-200/70">{empty || 'Ninguém mais no ranking ainda.'}</p>
      ) : rows.map((r, i) => {
        const rank = startAt + i;
        const prem = prize ? prize(rank) : null;
        return (
          <div key={`${rank}-${label(r)}`} data-testid={`${testid}-row-${rank}`} data-me={r.me ? '1' : undefined}
            className={`mb-1 flex items-center gap-2 rounded-lg border px-2 py-1.5 ${r.me ? 'wb-me-row' : rank <= 10 ? 'border-amber-700/60 bg-gradient-to-r from-purple-950/80 to-black' : 'border-purple-900/60 bg-black/40'}`}>
            <span className={`w-7 shrink-0 text-center text-[11px] font-black ${r.me ? 'text-lime-300' : rank <= 10 ? 'text-amber-300' : 'text-purple-300/70'}`}>{rank}º</span>
            <div className={`h-7 w-7 shrink-0 overflow-hidden rounded-full border ${r.me ? 'border-lime-400' : 'border-amber-700/60'}`}><AV row={r} /></div>
            <p className="min-w-0 flex-1 truncate text-[11px] font-black text-purple-50">
              {r.me && <span className="mr-1 rounded-sm bg-lime-400 px-1 text-[8px] text-black">VOCÊ</span>}{label(r)}
            </p>
            <div className="flex shrink-0 flex-col items-end leading-tight">
              <span className="text-[10px] font-black text-lime-300">{value(r)}</span>
              {prem && <span className="wb-prize-tag mt-0.5" data-testid={`${testid}-prize-${rank}`}><KakoCoin /> {prem}</span>}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

/* Sub-aba em placa de pedra (arte on/off) com o texto por cima. */
export const WbPlaqueTab = ({ on, label, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`relative min-w-0 flex-1 transition-transform active:scale-95 ${on ? '' : 'opacity-80'}`}>
    <img src={`/wb/plaque_${on ? 'on' : 'off'}.webp`} alt="" className="w-full drop-shadow-lg" />
    <span className={`wb-title absolute inset-0 flex items-center justify-center pt-[2%] text-[clamp(13px,4.6vw,22px)] ${on ? 'text-amber-200' : 'text-purple-300/80'}`}
      style={{ textShadow: on ? '0 0 10px rgba(249,115,22,.9), 0 2px 0 #3b0764' : '0 2px 0 #000' }}>{label}</span>
  </button>
);

/* Botão de regras (livro) no canto direito, logo abaixo das sub-abas — igual ao Oil Tycoon. */
export const WbRulesBtn = ({ onClick }) => (
  <div className="relative z-30 mx-auto -mb-10 flex max-w-[430px] justify-end pr-1">
    <button onClick={onClick} data-testid="wb-rank-rules-btn" aria-label="Regras do ranking"
      className="flex h-16 w-16 items-center justify-center transition-transform active:scale-90" style={{ animation: 'wb-rules-neon 2s ease-in-out infinite' }}>
      <img src="/wb/ic_regras.webp" alt="Regras do ranking" className="h-full w-full object-contain" />
    </button>
  </div>
);

/* Pílula do acumulado (arte JACKPOT + moedas), igual ao Oil Tycoon: só o valor e "Acumulado". */
export const WbPrizePill = ({ value, testid, fmt }) => {
  const [bump, setBump] = React.useState(false);
  const prev = React.useRef(value);
  React.useEffect(() => {
    if (Number(value) > Number(prev.current)) { setBump(true); const t = setTimeout(() => setBump(false), 1500); prev.current = value; return () => clearTimeout(t); }
    prev.current = value;
    return undefined;
  }, [value]);
  return (
    <div className="relative mx-auto mb-2 flex w-fit flex-col items-center rounded-2xl border border-amber-500/60 bg-black/70 px-5 pb-2 pt-1.5 backdrop-blur"
      style={{ animation: 'wb-glowpulse 2.4s ease-in-out infinite' }} data-testid={`${testid}-pill`}>
      <img src="/wb/title_jackpot.webp" alt="JACKPOT" className="w-[150px] max-w-full drop-shadow-xl" />
      <p className={`wb-jackpot-num flex items-center gap-1.5 text-lg font-black ${bump ? 'flash text-lime-300' : 'text-amber-200'}`} data-testid={testid}>
        <KakoCoin className="h-6" /> {fmt(value)} <span className="text-xs text-amber-400/80">moedas</span>
      </p>
      <p className="text-[9px] font-black uppercase tracking-[.3em] text-amber-500">Acumulado</p>
    </div>
  );
};
